import React, { useState, useEffect, useRef, useCallback } from 'react';
import io, { Socket } from 'socket.io-client';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { User, Message, VideoCall } from '../types';
import './Chat.css';
import config from '../config';

interface OnlineUser {
    oddrId: string;
    firstName: string;
    lastName?: string;
    major?: string;
}

interface ServerToClientEvents {
    'connect': () => void;
    'online-users': (users: OnlineUser[]) => void;
    'user-online': (data: { userId: string; firstName: string }) => void;
    'user-offline': (data: { userId: string }) => void;
    'receive-message': (message: Message) => void;
    'message-delivered': (data: { messageId: string; delivered: boolean }) => void;
    'incoming-video-call': (data: VideoCall) => void;
    'call-ringing': (data: { callId: string; recipientId: string }) => void;
    'call-started': (data: { callId: string; remoteUserId: string }) => void;
    'call-ended': (data: { callId: string; reason?: string; endedBy?: string }) => void;
    'call-error': (data: { message: string }) => void;
    'user-typing': (data: { userId: string; firstName: string }) => void;
    'user-stopped-typing': (data: { userId: string }) => void;
    'webrtc-offer': (data: { callId: string; offer: RTCSessionDescriptionInit; fromUserId: string }) => void;
    'webrtc-answer': (data: { callId: string; answer: RTCSessionDescriptionInit; fromUserId: string }) => void;
    'webrtc-ice-candidate': (data: { callId: string; candidate: RTCIceCandidateInit; fromUserId: string }) => void;
}

interface ClientToServerEvents {
    'send-message': (data: { recipientId: string; message: string; type?: string }) => void;
    'typing-start': (data: { recipientId: string }) => void;
    'typing-stop': (data: { recipientId: string }) => void;
    'video-call-request': (data: { targetUserId: string; callType: 'video' | 'audio' }) => void;
    'video-call-accept': (data: { callId: string }) => void;
    'video-call-end': (data: { callId: string; reason: string }) => void;
    'webrtc-offer': (data: { callId: string; offer: RTCSessionDescriptionInit; targetUserId: string }) => void;
    'webrtc-answer': (data: { callId: string; answer: RTCSessionDescriptionInit; targetUserId: string }) => void;
    'webrtc-ice-candidate': (data: { callId: string; candidate: RTCIceCandidateInit; targetUserId: string }) => void;
}

// WebRTC configuration with STUN servers
const rtcConfig: RTCConfiguration = {
    iceServers: [
        { urls: 'stun:stun.l.google.com:19302' },
        { urls: 'stun:stun1.l.google.com:19302' },
        { urls: 'stun:stun2.l.google.com:19302' },
    ]
};

const Chat: React.FC = () => {
    const [onlineUsers, setOnlineUsers] = useState<OnlineUser[]>([]);
    const [messages, setMessages] = useState<Message[]>([]);
    const [currentMessage, setCurrentMessage] = useState<string>('');
    const [loadingMessages, setLoadingMessages] = useState<boolean>(false);
    const [selectedUser, setSelectedUser] = useState<string | null>(null);
    const [incomingCall, setIncomingCall] = useState<VideoCall | null>(null);
    const [callActive, setCallActive] = useState<boolean>(false);
    const [currentCallId, setCurrentCallId] = useState<string | null>(null);
    const [remoteUserId, setRemoteUserId] = useState<string | null>(null);
    const [isTyping, setIsTyping] = useState<boolean>(false);
    const [userTyping, setUserTyping] = useState<{ userId: string; firstName: string } | null>(null);
    const [isCaller, setIsCaller] = useState<boolean>(false);
    
    const localVideoRef = useRef<HTMLVideoElement>(null);
    const remoteVideoRef = useRef<HTMLVideoElement>(null);
    const peerConnectionRef = useRef<RTCPeerConnection | null>(null);
    const localStreamRef = useRef<MediaStream | null>(null);
    const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const socketRef = useRef<Socket<ServerToClientEvents, ClientToServerEvents> | null>(null);
    const iceCandidatesQueue = useRef<RTCIceCandidateInit[]>([]);
    const currentCallIdRef = useRef<string | null>(null);
    const remoteUserIdRef = useRef<string | null>(null);
    const isCallerRef = useRef<boolean>(false);
    
    const navigate = useNavigate();
    
    const userStr = localStorage.getItem('user');
    const token = localStorage.getItem('token');
    const user: User | null = userStr ? JSON.parse(userStr) : null;

    // Cleanup function for ending calls
    const endCall = useCallback((): void => {
        console.log('Ending call...');
        
        if (localStreamRef.current) {
            localStreamRef.current.getTracks().forEach(track => track.stop());
            localStreamRef.current = null;
        }
        
        if (peerConnectionRef.current) {
            peerConnectionRef.current.close();
            peerConnectionRef.current = null;
        }
        
        if (localVideoRef.current) {
            localVideoRef.current.srcObject = null;
        }
        
        if (remoteVideoRef.current) {
            remoteVideoRef.current.srcObject = null;
        }
        
        iceCandidatesQueue.current = [];
        currentCallIdRef.current = null;
        remoteUserIdRef.current = null;
        isCallerRef.current = false;
        setCallActive(false);
        setCurrentCallId(null);
        setRemoteUserId(null);
        setIsCaller(false);
    }, []);

    // Create WebRTC peer connection
    const createPeerConnection = useCallback((remoteId: string, callId: string): RTCPeerConnection => {
        console.log('Creating peer connection...');
        const pc = new RTCPeerConnection(rtcConfig);

        // Add local tracks to connection
        if (localStreamRef.current) {
            localStreamRef.current.getTracks().forEach(track => {
                pc.addTrack(track, localStreamRef.current!);
            });
        }

        // Handle ICE candidates
        pc.onicecandidate = (event) => {
            if (event.candidate && socketRef.current) {
                console.log('Sending ICE candidate');
                socketRef.current.emit('webrtc-ice-candidate', {
                    callId,
                    candidate: event.candidate.toJSON(),
                    targetUserId: remoteId
                });
            }
        };

        // Handle remote stream
        pc.ontrack = (event) => {
            console.log('Received remote track');
            if (remoteVideoRef.current && event.streams[0]) {
                remoteVideoRef.current.srcObject = event.streams[0];
            }
        };

        // Handle connection state changes
        pc.onconnectionstatechange = () => {
            console.log('Connection state:', pc.connectionState);
            if (pc.connectionState === 'disconnected' || pc.connectionState === 'failed') {
                toast.error('Connection lost');
                endCall();
            }
        };

        pc.oniceconnectionstatechange = () => {
            console.log('ICE connection state:', pc.iceConnectionState);
        };

        return pc;
    }, [endCall]);

    // Process queued ICE candidates
    const processIceCandidates = useCallback(async () => {
        if (peerConnectionRef.current && peerConnectionRef.current.remoteDescription) {
            for (const candidate of iceCandidatesQueue.current) {
                try {
                    await peerConnectionRef.current.addIceCandidate(new RTCIceCandidate(candidate));
                    console.log('Added queued ICE candidate');
                } catch (err) {
                    console.error('Error adding queued ICE candidate:', err);
                }
            }
            iceCandidatesQueue.current = [];
        }
    }, []);

    // Start call as caller (create offer)
    const startCallAsCaller = useCallback(async (remoteId: string, callId: string) => {
        try {
            console.log('Starting call as caller...');
            const pc = createPeerConnection(remoteId, callId);
            peerConnectionRef.current = pc;

            const offer = await pc.createOffer();
            await pc.setLocalDescription(offer);

            if (socketRef.current) {
                socketRef.current.emit('webrtc-offer', {
                    callId,
                    offer: pc.localDescription!,
                    targetUserId: remoteId
                });
            }
        } catch (err) {
            console.error('Error starting call as caller:', err);
            toast.error('Failed to start call');
            endCall();
        }
    }, [createPeerConnection, endCall]);

    // Handle incoming offer (as receiver)
    const handleOffer = useCallback(async (callId: string, offer: RTCSessionDescriptionInit, fromUserId: string) => {
        try {
            console.log('Handling incoming offer...');
            
            if (!peerConnectionRef.current) {
                const pc = createPeerConnection(fromUserId, callId);
                peerConnectionRef.current = pc;
            }

            await peerConnectionRef.current.setRemoteDescription(new RTCSessionDescription(offer));
            
            const answer = await peerConnectionRef.current.createAnswer();
            await peerConnectionRef.current.setLocalDescription(answer);

            if (socketRef.current) {
                socketRef.current.emit('webrtc-answer', {
                    callId,
                    answer: peerConnectionRef.current.localDescription!,
                    targetUserId: fromUserId
                });
            }

            // Process any queued ICE candidates
            await processIceCandidates();
        } catch (err) {
            console.error('Error handling offer:', err);
            toast.error('Failed to connect call');
            endCall();
        }
    }, [createPeerConnection, processIceCandidates, endCall]);

    // Handle incoming answer (as caller)
    const handleAnswer = useCallback(async (answer: RTCSessionDescriptionInit) => {
        try {
            console.log('Handling incoming answer...');
            if (peerConnectionRef.current) {
                await peerConnectionRef.current.setRemoteDescription(new RTCSessionDescription(answer));
                await processIceCandidates();
            }
        } catch (err) {
            console.error('Error handling answer:', err);
        }
    }, [processIceCandidates]);

    // Handle incoming ICE candidate
    const handleIceCandidate = useCallback(async (candidate: RTCIceCandidateInit) => {
        try {
            if (peerConnectionRef.current && peerConnectionRef.current.remoteDescription) {
                await peerConnectionRef.current.addIceCandidate(new RTCIceCandidate(candidate));
                console.log('Added ICE candidate');
            } else {
                console.log('Queueing ICE candidate');
                iceCandidatesQueue.current.push(candidate);
            }
        } catch (err) {
            console.error('Error adding ICE candidate:', err);
        }
    }, []);

    // Main socket connection effect
    useEffect(() => {
        if (!token || !user) {
            navigate('/login');
            return;
        }

        if (socketRef.current) {
            return;
        }

        const newSocket = io(config.SOCKET_URL, {
            auth: { token },
            reconnection: true,
            reconnectionAttempts: 5,
            reconnectionDelay: 1000,
        }) as Socket<ServerToClientEvents, ClientToServerEvents>;

        newSocket.on('connect', () => {
            console.log('Connected to server');
            toast.success('Connected!');
        });

        newSocket.on('online-users', (users: OnlineUser[]) => {
            console.log('Online users received:', users);
            setOnlineUsers(users.filter(u => u && u.oddrId !== user.id));
        });

        newSocket.on('user-online', (data) => {
            if (data.userId !== user.id) {
                toast(`${data.firstName} is online`, { icon: '🟢' });
                setOnlineUsers(prev => {
                    if (prev.some(u => u.oddrId === data.userId)) return prev;
                    return [...prev, { oddrId: data.userId, firstName: data.firstName }];
                });
            }
        });

        newSocket.on('user-offline', (data) => {
            setOnlineUsers(prev => prev.filter(u => u.oddrId !== data.userId));
        });

        newSocket.on('receive-message', (message: Message) => {
            setMessages(prev => [...prev, message]);
        });

        newSocket.on('incoming-video-call', (data: VideoCall) => {
            console.log('Incoming call:', data);
            setIncomingCall(data);
            toast(`${data.callerName} is calling...`, { icon: '📞', duration: 10000 });
        });

        newSocket.on('call-ringing', (data) => {
            console.log('Call ringing:', data);
            currentCallIdRef.current = data.callId;
            remoteUserIdRef.current = data.recipientId;
            isCallerRef.current = true;
            setCurrentCallId(data.callId);
            setRemoteUserId(data.recipientId);
            setIsCaller(true);
        });

        newSocket.on('call-started', (data) => {
            console.log('Call started:', data);
            // Update refs immediately
            if (!currentCallIdRef.current) {
                currentCallIdRef.current = data.callId;
                setCurrentCallId(data.callId);
            }
            if (!remoteUserIdRef.current) {
                remoteUserIdRef.current = data.remoteUserId;
                setRemoteUserId(data.remoteUserId);
            }
            setCallActive(true);
            
            // If caller, start WebRTC now
            if (isCallerRef.current && !peerConnectionRef.current) {
                console.log('Caller starting WebRTC after call-started');
                // Delay slightly to ensure state is ready
                setTimeout(() => {
                    if (remoteUserIdRef.current && currentCallIdRef.current && !peerConnectionRef.current) {
                        startCallAsCaller(remoteUserIdRef.current, currentCallIdRef.current);
                    }
                }, 100);
            }
        });

        newSocket.on('call-ended', () => {
            toast('Call ended');
            endCall();
        });

        newSocket.on('call-error', (data) => {
            toast.error(data.message);
            // Reset call state on error
            if (peerConnectionRef.current) {
                peerConnectionRef.current.close();
                peerConnectionRef.current = null;
            }
            if (localStreamRef.current) {
                localStreamRef.current.getTracks().forEach(track => track.stop());
                localStreamRef.current = null;
            }
            iceCandidatesQueue.current = [];
            currentCallIdRef.current = null;
            remoteUserIdRef.current = null;
            isCallerRef.current = false;
            setCallActive(false);
            setCurrentCallId(null);
            setRemoteUserId(null);
            setIsCaller(false);
        });

        newSocket.on('webrtc-offer', (data) => {
            console.log('Received WebRTC offer');
            handleOffer(data.callId, data.offer, data.fromUserId);
        });

        newSocket.on('webrtc-answer', (data) => {
            console.log('Received WebRTC answer');
            handleAnswer(data.answer);
        });

        newSocket.on('webrtc-ice-candidate', (data) => {
            console.log('Received ICE candidate');
            handleIceCandidate(data.candidate);
        });

        socketRef.current = newSocket;

        return () => {
            if (socketRef.current) {
                socketRef.current.disconnect();
                socketRef.current = null;
            }
        };
    }, []);

    // Effect to start WebRTC when call becomes active (for caller) - REMOVED
    // Now handled directly in call-started event handler

    // Typing indicator effect
    useEffect(() => {
        if (!socketRef.current) return;
        
        const handleTyping = (data: { userId: string; firstName: string }) => {
            if (data.userId === selectedUser) {
                setUserTyping(data);
            }
        };

        const handleStoppedTyping = (data: { userId: string }) => {
            if (data.userId === selectedUser) {
                setUserTyping(null);
            }
        };

        socketRef.current.on('user-typing', handleTyping);
        socketRef.current.on('user-stopped-typing', handleStoppedTyping);

        return () => {
            socketRef.current?.off('user-typing', handleTyping);
            socketRef.current?.off('user-stopped-typing', handleStoppedTyping);
        };
    }, [selectedUser]);

    if (!user || !token) {
        return null;
    }

    const sendMessage = (): void => {
        if (currentMessage.trim() && selectedUser && socketRef.current) {
            socketRef.current.emit('send-message', {
                recipientId: selectedUser,
                message: currentMessage,
                type: 'text'
            });
            
            setMessages(prev => [...prev, {
                id: Date.now().toString(),
                senderId: user.id,
                senderName: 'You',
                recipientId: selectedUser,
                message: currentMessage,
                type: 'text',
                timestamp: new Date(),
                delivered: false,
                read: false
            } as Message]);
            
            setCurrentMessage('');
        }
    };

    const handleTypingInput = (value: string): void => {
        setCurrentMessage(value);
        
        if (socketRef.current && selectedUser && value.length > 0 && !isTyping) {
            socketRef.current.emit('typing-start', { recipientId: selectedUser });
            setIsTyping(true);
        }
        
        if (typingTimeoutRef.current) {
            clearTimeout(typingTimeoutRef.current);
        }
        
        typingTimeoutRef.current = setTimeout(() => {
            if (socketRef.current && selectedUser && isTyping) {
                socketRef.current.emit('typing-stop', { recipientId: selectedUser });
                setIsTyping(false);
            }
        }, 1000);
    };

    const startVideoCall = async (): Promise<void> => {
        if (!selectedUser || !socketRef.current) return;
        
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ 
                video: true, 
                audio: true 
            });
            
            localStreamRef.current = stream;
            if (localVideoRef.current) {
                localVideoRef.current.srcObject = stream;
            }

            // Set refs
            remoteUserIdRef.current = selectedUser;
            isCallerRef.current = true;

            socketRef.current.emit('video-call-request', {
                targetUserId: selectedUser,
                callType: 'video'
            });
            
            setRemoteUserId(selectedUser);
            setIsCaller(true);
            setCallActive(true);
            toast('Calling...');
        } catch (err) {
            console.error('Error accessing media:', err);
            toast.error('Cannot access camera/microphone');
        }
    };

    const acceptCall = async (): Promise<void> => {
        if (!incomingCall || !socketRef.current) return;
        
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ 
                video: true, 
                audio: true 
            });
            
            localStreamRef.current = stream;
            if (localVideoRef.current) {
                localVideoRef.current.srcObject = stream;
            }

            // Set refs before emitting
            currentCallIdRef.current = incomingCall.callId;
            remoteUserIdRef.current = incomingCall.callerId;
            isCallerRef.current = false;

            socketRef.current.emit('video-call-accept', { 
                callId: incomingCall.callId 
            });
            
            setCurrentCallId(incomingCall.callId);
            setRemoteUserId(incomingCall.callerId);
            setIsCaller(false);
            setIncomingCall(null);
            setCallActive(true);
        } catch (err) {
            console.error('Error accessing media:', err);
            toast.error('Cannot access camera/microphone');
        }
    };

    const rejectCall = (): void => {
        if (incomingCall && socketRef.current) {
            socketRef.current.emit('video-call-end', {
                callId: incomingCall.callId,
                reason: 'rejected'
            });
        }
        setIncomingCall(null);
    };

    const handleEndCall = (): void => {
        if (currentCallId && socketRef.current) {
            socketRef.current.emit('video-call-end', {
                callId: currentCallId,
                reason: 'ended'
            });
        }
        endCall();
    };

    const logout = (): void => {
        endCall();
        if (socketRef.current) {
            socketRef.current.disconnect();
        }
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/login');
    };

    const filteredMessages = messages.filter(m => 
        (m.senderId === user.id && m.recipientId === selectedUser) ||
        (m.senderId === selectedUser && m.recipientId === user.id)
    );

    const getSelectedUserName = (): string => {
        const found = onlineUsers.find(u => u.oddrId === selectedUser);
        return found?.firstName || 'User';
    };

    const selectUser = async (userId: string): Promise<void> => {
        setSelectedUser(userId);
        setLoadingMessages(true);
        
        try {
            const response = await fetch(`${config.API_URL}/messages/conversation/${userId}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            const data = await response.json();
            
            if (data.success && data.messages) {
                const formattedMessages: Message[] = data.messages.map((msg: any) => ({
                    id: msg._id,
                    senderId: msg.sender,
                    senderName: msg.sender === user?.id ? 'You' : getSelectedUserName(),
                    recipientId: msg.recipient,
                    message: msg.content,
                    type: msg.type,
                    timestamp: new Date(msg.createdAt),
                    delivered: msg.delivered,
                    read: msg.read
                }));
                setMessages(formattedMessages);
            }
        } catch (error) {
            console.error('Error fetching messages:', error);
            toast.error('Failed to load messages');
        } finally {
            setLoadingMessages(false);
        }
    };

    return (
        <div className="chat-container">
            <div className="sidebar">
                <div className="user-info">
                    <h3>{user.profile.firstName} {user.profile.lastName}</h3>
                    <p>{user.profile.major} - {user.profile.year}</p>
                    <button onClick={logout}>Logout</button>
                </div>
                
                <div className="online-users">
                    <h4>ONLINE USERS ({onlineUsers.length})</h4>
                    {onlineUsers.length === 0 ? (
                        <p style={{ color: '#95a5a6', padding: '10px' }}>No users online</p>
                    ) : (
                        onlineUsers.map(onlineUser => (
                            <div 
                                key={onlineUser.oddrId}
                                className={`user-item ${selectedUser === onlineUser.oddrId ? 'selected' : ''}`}
                                onClick={() => selectUser(onlineUser.oddrId)}
                            >
                                🟢 {onlineUser.firstName} {onlineUser.lastName || ''}
                            </div>
                        ))
                    )}
                </div>
            </div>

            <div className="main-chat">
                {selectedUser ? (
                    <>
                        <div className="chat-header">
                            <div>
                                <h3>Chatting with {getSelectedUserName()}</h3>
                                {userTyping && <p style={{ fontSize: '12px', color: '#7f8c8d' }}>
                                    {userTyping.firstName} is typing...
                                </p>}
                            </div>
                            <button onClick={startVideoCall} disabled={callActive}>
                                📹 Video Call
                            </button>
                        </div>

                        <div className="messages">
                            {loadingMessages ? (
                                <p style={{ textAlign: 'center', color: '#7f8c8d' }}>Loading messages...</p>
                            ) : filteredMessages.length === 0 ? (
                                <p style={{ textAlign: 'center', color: '#7f8c8d' }}>No messages yet. Say hi! 👋</p>
                            ) : (
                                filteredMessages.map((msg, index) => (
                                    <div key={index} className={`message ${msg.senderId === user.id ? 'sent' : 'received'}`}>
                                        <strong>{msg.senderName}:</strong> {msg.message}
                                        <span className="timestamp">
                                            {new Date(msg.timestamp).toLocaleTimeString()}
                                        </span>
                                    </div>
                                ))
                            )}
                        </div>

                        <div className="message-input">
                            <input
                                type="text"
                                value={currentMessage}
                                onChange={(e) => handleTypingInput(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                                placeholder="Type a message..."
                            />
                            <button onClick={sendMessage}>Send</button>
                        </div>
                    </>
                ) : (
                    <div className="no-selection">
                        <h2>Welcome to NU Connect!</h2>
                        <p>Connect with fellow Northeastern students</p>
                        <p>Select a user from the sidebar to start chatting</p>
                    </div>
                )}
            </div>

            {/* Incoming Call Modal */}
            {incomingCall && !callActive && (
                <div className="video-modal">
                    <div className="incoming-call">
                        <h2>Incoming Call</h2>
                        <p>{incomingCall.callerName} is calling...</p>
                        <p>{incomingCall.callerMajor}</p>
                        <div className="call-actions">
                            <button className="accept-btn" onClick={acceptCall}>✅ Accept</button>
                            <button className="reject-btn" onClick={rejectCall}>❌ Reject</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Active Video Call */}
            {callActive && (
                <div className="video-modal">
                    <div className="video-container">
                        <video ref={remoteVideoRef} autoPlay playsInline className="remote-video" />
                        <video 
                            ref={(el) => {
                                localVideoRef.current = el;
                                // Ensure local stream is attached when element mounts
                                if (el && localStreamRef.current && !el.srcObject) {
                                    el.srcObject = localStreamRef.current;
                                }
                            }} 
                            autoPlay 
                            playsInline 
                            muted 
                            className="local-video" 
                        />
                        <div className="video-controls">
                            <button className="end-call-btn" onClick={handleEndCall}>
                                📞 End Call
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Chat;